import React, { createContext, useContext, useRef, useState, useMemo, useEffect } from 'react';

export const AppBarContext = createContext(null);

/**
 * AppBarProvider
 * Maintains a registry of per-page AppBar configs keyed by monotonic id.
 * The config with the highest id (most recently registered page) is "current".
 */
export function AppBarProvider({ children }) {
  // Use a ref for the Map so mutations don't trigger re-render by themselves.
  // A separate version counter forces re-renders when the Map changes.
  const mapRef = useRef(new Map());
  const [version, setVersion] = useState(0);
  const idCounterRef = useRef(0);

  const bumpVersion = () => setVersion((v) => v + 1);

  const register = (config) => {
    const id = ++idCounterRef.current;
    mapRef.current.set(id, config);
    bumpVersion();
    return id;
  };

  const update = (id, config) => {
    if (mapRef.current.has(id)) {
      mapRef.current.set(id, config);
      bumpVersion();
    }
  };

  const unregister = (id) => {
    if (mapRef.current.has(id)) {
      mapRef.current.delete(id);
      bumpVersion();
    }
  };

  // currentConfig: entry with the highest Map key, or null.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentConfig = useMemo(() => {
    if (mapRef.current.size === 0) return null;
    let highestId = -Infinity;
    let result = null;
    mapRef.current.forEach((cfg, id) => {
      if (id > highestId) {
        highestId = id;
        result = cfg;
      }
    });
    return result;
  }, [version]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(
    () => ({ register, update, unregister, currentConfig }),
    [currentConfig] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return <AppBarContext.Provider value={value}>{children}</AppBarContext.Provider>;
}

/**
 * useAppBarConfig
 * Called from page components to register/update/unregister their AppBar config.
 * Safe to call outside a provider — acts as a no-op.
 *
 * @param {{ title, subtitle, backTo, action }} config
 * @param {any[]} [deps=[]] — additional deps beyond the config props themselves
 */
export function useAppBarConfig({ title, subtitle, backTo, action }, deps = []) {
  const ctx = useContext(AppBarContext);
  const idRef = useRef(null);

  // Stable config object — avoid triggering the effect on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const config = useMemo(
    () => ({ title, subtitle, backTo, action }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [title, subtitle, backTo, action, ...deps]
  );

  useEffect(() => {
    // No-op when used outside a provider
    if (!ctx) return;

    if (idRef.current === null) {
      // First mount — register
      idRef.current = ctx.register(config);
    } else {
      // Subsequent renders — update
      ctx.update(idRef.current, config);
    }

    return () => {
      if (ctx && idRef.current !== null) {
        ctx.unregister(idRef.current);
        idRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);
}

/**
 * useAppBarState
 * Returns the currentConfig from the nearest AppBarProvider, or null if none.
 */
export function useAppBarState() {
  const ctx = useContext(AppBarContext);
  return ctx ? ctx.currentConfig : null;
}
